import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraText } from '../ensigns/typography';
import { chartPlotGutters, chartToneVar, SpecraChartFrame, SpecraChartLegend, SpecraChartLink } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
const percent = (v)=>`${v}%`;
/**
 * The value axis: whole steps of 1, 2, 5, 10, 20, 25 or 50, at most four of
 * them, from just under the lowest value (or the given bottom) to the top.
 */ export function axisOf(lowest, max, min) {
    if (min !== undefined) return {
        min,
        step: (max - min) / 4 || 1
    };
    const room = Math.min(max, Math.max(max - (lowest - 1), 1));
    for (const step of [
        1,
        2,
        5,
        10,
        20,
        25,
        50
    ]){
        const n = Math.ceil(room / step);
        if (n <= 4) return {
            min: Math.max(0, max - n * step),
            step
        };
    }
    return {
        min: 0,
        step: max / 4
    };
}
const NICE = [
    1,
    2,
    5,
    10
];
/** The smallest round step (1, 2 or 5 × 10ⁿ) at least `raw`. */ function niceAtLeast(raw) {
    const mag = 10 ** Math.floor(Math.log10(raw));
    return (NICE.find((m)=>m * mag >= raw * (1 - 1e-9)) ?? 10) * mag;
}
/**
 * The value axis for given bounds: a round step, the bounds widened to it,
 * and no two ticks printing the same label (99.6 and 99.8 both reading
 * "100%" would make the axis lie).
 */ export function niceAxis(lo, hi, format) {
    // No values yet (a chart still streaming in): no ticks over the ghost axes.
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return {
        min: 0,
        max: 1,
        ticks: []
    };
    const span = hi - lo || Math.abs(hi) || 1;
    let step = niceAtLeast(span / 4);
    for(let tries = 0; tries < 12; tries++){
        const decimals = Math.max(0, -Math.floor(Math.log10(step)) + (step % 1 ? 1 : 0));
        const min = Math.floor(lo / step + 1e-9) * step;
        const max = Math.ceil(hi / step - 1e-9) * step;
        const ticks = [];
        for(let i = 0; min + i * step <= max + step / 2; i++){
            ticks.push(Number((min + i * step).toFixed(decimals)));
        }
        const labels = ticks.map(format);
        if (ticks.length > 1 && new Set(labels).size === labels.length) {
            return {
                min: ticks[0],
                max: ticks[ticks.length - 1],
                ticks
            };
        }
        step = niceAtLeast(step * 1.0001);
    }
    return {
        min: lo,
        max: hi,
        ticks: [
            lo,
            hi
        ]
    };
}
/** A rate per period as a line against its target, with an optional count strip under it. */ export function SpecraTrendChart({ points, seriesLabel, target, format = percent, min: minProp, max: maxProp = 100, columns, labelEvery = 1, emptyMessage, renderLink, animate = false, className, getPointTestId, getColumnTestId, title, description, actions, insight, 'data-testid': testId }) {
    const n = points.length;
    const labelRowRef = React.useRef(null);
    const [rowWidth, setRowWidth] = React.useState(0);
    React.useEffect(()=>{
        const el = labelRowRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(([e])=>setRowWidth(e.contentRect.width));
        ro.observe(el);
        return ()=>ro.disconnect();
    }, []);
    // About 6px a character at the micro size, plus a gap.
    const labelWidth = Math.max(0, ...points.map((p)=>p.label.length)) * 6 + 12;
    const every = Math.max(labelEvery, rowWidth ? Math.ceil(labelWidth * n / rowWidth) : 1);
    const values = points.map((p)=>p.value).filter((v)=>v !== null);
    const empty = values.length === 0;
    const lowest = Math.min(...values, target?.value ?? maxProp, maxProp);
    // Given bounds (chat charts) get round ticks; the rate default keeps its
    // whole-percent steps down from 100.
    const { min, max, ticks } = (()=>{
        if (minProp !== undefined) return niceAxis(minProp, maxProp, format);
        const axis = axisOf(lowest, maxProp);
        const ts = [];
        for(let t = axis.min; t <= maxProp + 1e-9; t += axis.step)ts.push(Math.round(t));
        return {
            min: axis.min,
            max: maxProp,
            ticks: ts
        };
    })();
    const span = max - min || 1;
    const x = (i)=>(i + 0.5) / Math.max(n, 1) * 100;
    const y = (v)=>(max - Math.min(Math.max(v, min), max)) / span * 100;
    // The line breaks over a period with no value.
    const runs = [];
    points.forEach((p, i)=>{
        if (p.value === null) return;
        const prev = i > 0 ? points[i - 1] : undefined;
        if (!prev || prev.value === null || runs.length === 0) runs.push([]);
        runs[runs.length - 1].push({
            i,
            v: p.value
        });
    });
    const lastIndex = points.reduce((at, p, i)=>p.value !== null ? i : at, -1);
    const table = {
        columns: [
            {
                key: 'period',
                label: 'Period'
            },
            {
                key: 'value',
                label: seriesLabel,
                numeric: true
            },
            ...columns ? [
                {
                    key: 'count',
                    label: columns.label,
                    numeric: true
                }
            ] : []
        ],
        rows: points.map((p, i)=>({
                id: p.id,
                cells: {
                    period: p.title,
                    value: p.value === null ? 'No data' : format(p.value),
                    count: columns?.items[i]?.value.toLocaleString() ?? ''
                }
            }))
    };
    const legendItems = [
        {
            label: seriesLabel,
            tone: 'series',
            shape: 'line'
        },
        ...target ? [
            {
                label: target.label,
                tone: 'good',
                shape: 'line'
            }
        ] : []
    ];
    return /*#__PURE__*/ _jsx(SpecraChartFrame, {
        actions: actions,
        className: className,
        "data-testid": testId,
        description: description,
        insight: empty ? undefined : insight,
        legend: empty ? undefined : /*#__PURE__*/ _jsx(SpecraChartLegend, {
            items: legendItems
        }),
        table: table,
        title: title,
        children: /*#__PURE__*/ _jsx(SpecraTooltipProvider, {
            delayDuration: 100,
            children: /*#__PURE__*/ _jsxs("div", {
                className: cn('relative', chartPlotGutters),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative h-[var(--component-chart-plot-height)]",
                        children: [
                            ticks.map((t)=>/*#__PURE__*/ _jsx(SpecraText, {
                                    "aria-hidden": "true",
                                    className: "absolute -translate-y-1/2 tabular-nums right-full pr-[var(--space-1-5)]",
                                    size: "micro",
                                    style: {
                                        top: `${y(t)}%`
                                    },
                                    tone: "muted",
                                    children: format(t)
                                }, t)),
                            /*#__PURE__*/ _jsxs("svg", {
                                "aria-hidden": "true",
                                className: cn('absolute inset-0 size-full overflow-visible', animate && 'specra-line-draw'),
                                preserveAspectRatio: "none",
                                viewBox: "0 0 100 100",
                                children: [
                                    ticks.map((t)=>/*#__PURE__*/ _jsx("line", {
                                            stroke: "var(--chart-grid)",
                                            strokeWidth: 1,
                                            vectorEffect: "non-scaling-stroke",
                                            x1: 0,
                                            x2: 100,
                                            y1: y(t),
                                            y2: y(t)
                                        }, t)),
                                    target && !empty ? /*#__PURE__*/ _jsx("line", {
                                        "data-chart-target": "",
                                        stroke: chartToneVar.good,
                                        strokeWidth: 1.5,
                                        vectorEffect: "non-scaling-stroke",
                                        x1: 0,
                                        x2: 100,
                                        y1: y(target.value),
                                        y2: y(target.value)
                                    }) : null,
                                    runs.map((run)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                                            children: [
                                                run.length > 1 ? /*#__PURE__*/ _jsx("path", {
                                                    d: `M${x(run[0].i)} 100 ${run.map((p)=>`L${x(p.i)} ${y(p.v)}`).join(' ')} L${x(run[run.length - 1].i)} 100Z`,
                                                    fill: chartToneVar.series,
                                                    fillOpacity: 0.08
                                                }) : null,
                                                /*#__PURE__*/ _jsx("path", {
                                                    d: run.map((p, k)=>`${k ? 'L' : 'M'}${x(p.i)} ${y(p.v)}`).join(' '),
                                                    fill: "none",
                                                    stroke: chartToneVar.series,
                                                    strokeLinecap: "round",
                                                    strokeLinejoin: "round",
                                                    strokeWidth: 2,
                                                    vectorEffect: "non-scaling-stroke"
                                                })
                                            ]
                                        }, run[0].i))
                                ]
                            }),
                            target && !empty ? /*#__PURE__*/ _jsx(SpecraText, {
                                "aria-hidden": "true",
                                className: "absolute left-full -translate-y-1/2 whitespace-nowrap pl-[var(--space-1-5)] text-success",
                                size: "micro",
                                style: {
                                    top: `${y(target.value)}%`
                                },
                                children: "Target"
                            }) : null,
                            lastIndex >= 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                "aria-hidden": "true",
                                className: "absolute left-full -translate-y-1/2 pl-[var(--space-1-5)] font-semibold tabular-nums",
                                size: "meta",
                                style: {
                                    top: `${y(points[lastIndex].value ?? 0) + (target && Math.abs(y(target.value) - y(points[lastIndex].value ?? 0)) < 9 ? 9 : 0)}%`
                                },
                                children: format(points[lastIndex].value ?? 0)
                            }) : null,
                            empty ? /*#__PURE__*/ _jsx("div", {
                                className: "absolute inset-0 flex items-center justify-center px-[var(--space-4)]",
                                children: /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    className: "max-w-sm rounded-[var(--radius-md)] bg-[var(--surface-card)] px-[var(--space-2)] py-[var(--space-1)] text-center",
                                    "data-chart-empty": "",
                                    size: "body-ui",
                                    tone: "muted",
                                    children: emptyMessage
                                })
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "absolute inset-0 flex",
                                children: points.map((p, i)=>{
                                    const point = /*#__PURE__*/ _jsxs(SpecraChartLink, {
                                        "aria-label": `${p.title}: ${p.value === null ? 'no data' : format(p.value)}${p.detail ? `, ${p.detail}` : ''}`,
                                        className: "group relative h-full flex-1",
                                        "data-item-id": p.id,
                                        "data-testid": getPointTestId?.(p),
                                        href: p.href,
                                        renderLink: renderLink,
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                "aria-hidden": "true",
                                                className: "absolute inset-y-0 left-1/2 w-px bg-border-strong opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                                            }),
                                            p.value !== null ? /*#__PURE__*/ _jsx("span", {
                                                "aria-hidden": "true",
                                                className: cn('absolute left-1/2 size-[var(--component-chart-dot)] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[color:var(--surface-card)] bg-[var(--chart-series)] transition-transform group-hover:scale-125 group-focus-visible:scale-125', i === lastIndex && 'scale-125'),
                                                style: {
                                                    top: `${y(p.value)}%`
                                                }
                                            }) : null
                                        ]
                                    });
                                    return /*#__PURE__*/ _jsx(SpecraTooltip, {
                                        content: /*#__PURE__*/ _jsxs("span", {
                                            className: "block",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "block font-semibold",
                                                    children: p.value === null ? 'No data' : format(p.value)
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "block",
                                                    children: p.title
                                                }),
                                                p.detail ? /*#__PURE__*/ _jsx("span", {
                                                    className: "block",
                                                    children: p.detail
                                                }) : null
                                            ]
                                        }),
                                        delayDuration: 100,
                                        children: point
                                    }, p.id);
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": "true",
                        className: "mt-[var(--space-1)] flex",
                        ref: labelRowRef,
                        children: points.map((p, i)=>/*#__PURE__*/ _jsx(SpecraText, {
                                className: "flex min-w-0 flex-1 basis-0 justify-center whitespace-nowrap",
                                size: "micro",
                                tone: "muted",
                                children: (every === labelEvery ? i % every === 0 || i === n - 1 : (n - 1 - i) % every === 0) ? p.label : ''
                            }, p.id))
                    }),
                    columns && !empty ? /*#__PURE__*/ _jsx(TrendColumns, {
                        getColumnTestId: getColumnTestId,
                        items: columns.items,
                        label: columns.label,
                        renderLink: renderLink
                    }) : null
                ]
            })
        })
    });
}
function TrendColumns({ label, items, renderLink, getColumnTestId }) {
    const top = Math.max(1, ...items.map((c)=>c.value));
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-[var(--space-3)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                size: "meta",
                tone: "muted",
                children: label
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative mt-[var(--space-1)] flex h-[var(--component-chart-strip-height)] items-end border-b border-border-strong",
                children: items.map((c)=>/*#__PURE__*/ _jsx(SpecraTooltip, {
                        content: /*#__PURE__*/ _jsxs("span", {
                            className: "block",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "block font-semibold",
                                    children: c.value.toLocaleString()
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "block",
                                    children: c.title
                                }),
                                c.detail ? /*#__PURE__*/ _jsx("span", {
                                    className: "block",
                                    children: c.detail
                                }) : null
                            ]
                        }),
                        delayDuration: 100,
                        children: /*#__PURE__*/ _jsxs(SpecraChartLink, {
                            "aria-label": `${c.title}: ${c.value.toLocaleString()} ${label.toLowerCase()}${c.detail ? `, ${c.detail}` : ''}`,
                            className: "group relative flex h-full flex-1 items-end justify-center rounded-t-[var(--radius-sm)] hover:bg-[var(--surface-hover)]",
                            "data-item-id": c.id,
                            "data-testid": getColumnTestId?.(c),
                            href: c.href,
                            renderLink: renderLink,
                            children: [
                                c.labelled && c.value > 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                    "aria-hidden": "true",
                                    className: "absolute left-1/2 -translate-x-1/2 font-semibold tabular-nums",
                                    size: "micro",
                                    style: {
                                        bottom: `calc(${c.value / top * 78}% + var(--space-0-5))`
                                    },
                                    children: c.value.toLocaleString()
                                }) : null,
                                /*#__PURE__*/ _jsx("span", {
                                    "aria-hidden": "true",
                                    className: "block w-[var(--space-3-5)] rounded-t-[var(--radius-sm)] transition-opacity group-hover:opacity-80",
                                    style: {
                                        height: c.value > 0 ? `max(${c.value / top * 78}%, var(--space-0-5))` : 0,
                                        background: chartToneVar[c.tone ?? 'pending']
                                    }
                                })
                            ]
                        })
                    }, c.id))
            })
        ]
    });
}
